import { inject, Injectable } from '@angular/core';
import { keys, mapKeys } from '@sama/micro-dash';
import { forEach } from 'lodash';
import { deleteShapes } from '../../../cross-team-shared/annotation/shared/state/layers/shape-layer-state';
import { ShapeState } from '../../../cross-team-shared/annotation/shared/state/shape-state';
import { MessagingService } from '../../../cross-team-shared/messaging/messaging.service';
import { setDefaultTags } from '../../../cross-team-shared/misc-services/tag-helper';
import { keys as numberKeys } from '../../../cross-team-shared/misc-utils/keys';
import { isPointCloudOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import { strictValues } from '../../../cross-team-shared/misc-utils/strict-record';
import { addUngroupedShape } from '../../../cross-team-shared/state/shape.state';
import { pickExisting } from '../../../shared/misc-utilities/misc-utils';
import { ShapeGroupOrderState } from '../../../team-apis/content/content-connector';
import { AnnotationOutput } from '../../../team-apis/otterdam/outputs/annotation-output';
import { PointCloudOutput } from '../../../team-apis/otterdam/outputs/point-cloud-output';
import {
  getFusedAnswerOptions,
  getFusedNonOrthographicVideoOutputs,
  hasFusedAnswerOptions,
} from '../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import {
  getAnnotationOutput,
  getOutputAnnotationInfoStore,
} from '../../h3ml/state/task-workspace-store-utils';
import { FusionManagerFactory } from '../../outputs/annotation/annotation-shared/fusion/fusion-manager-factory.service';
import { buildRectanglePoints } from '../../outputs/annotation/annotation-shared/fusion/projection/projection-utils';

@Injectable()
export class ObjectlessShapeService {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private messagingService = inject(MessagingService);
  private fusionManagerFactory = inject(FusionManagerFactory);

  createShape(
    shape: ShapeState,
    annotationOutputName: string,
    selectFirstTag = false,
  ) {
    const output = getAnnotationOutput(
      this.taskWorkspaceStore,
      annotationOutputName,
    );

    if (selectFirstTag) {
      setDefaultTags(
        shape,
        output.answer_options,
        this.taskWorkspaceStore.state().project.outputs,
        this.getFrameNumber(output.id),
      );
    }
    this.taskWorkspaceStore.batch(() => {
      const outputAnnotationInfoStore = getOutputAnnotationInfoStore(
        this.taskWorkspaceStore,
        output.name,
      );
      addUngroupedShape(shape, outputAnnotationInfoStore);
      if (isPointCloudOutput(output)) {
        createPlaceholderShapes(this.taskWorkspaceStore, output, shape);
      }
    });
    return shape;
  }

  updateShapes(
    shapeGroupOrderState: ShapeGroupOrderState,
    annotationOutput: AnnotationOutput,
  ) {
    const shapes = strictValues(shapeGroupOrderState.shapes);
    const outputStore = getOutputAnnotationInfoStore(
      this.taskWorkspaceStore,
      annotationOutput.name,
    );
    outputStore('shapes').assign(mapKeys(shapes, (shape) => shape.index));
  }

  deleteShapes(shapeIndices: number[], showMessage = true) {
    this.taskWorkspaceStore.batch(() => {
      this.removeFromSelection(shapeIndices);
      this.removeTransientInfo(shapeIndices);
      keys(this.taskWorkspaceStore.state().outputAnnotationInfo).forEach(
        (annotationOutputName) =>
          deleteShapes(
            shapeIndices,
            // @ts-ignore -- fix me
            this.taskWorkspaceStore('outputAnnotationInfo')(
              annotationOutputName,
            ),
          ),
      );
    });
    if (showMessage) {
      this.messagingService.show(`${shapeIndices.length} item(s) deleted`);
    }
  }

  getAllShapeIndices() {
    const { outputAnnotationInfo } = this.taskWorkspaceStore.state();
    const shapeIndices: number[] = [];

    forEach(outputAnnotationInfo, (shapeInfoForOutput) => {
      // @ts-ignore -- fix me
      shapeIndices.push(...numberKeys(shapeInfoForOutput.shapes));
    });

    return shapeIndices;
  }

  private removeFromSelection(shapeIndices: number[]) {
    const { fusions } = this.taskWorkspaceStore.state();

    if (!this.taskWorkspaceStore.isUsingObjects()) {
      return;
    }

    const fusionOutputId = numberKeys(fusions)[0];
    this.taskWorkspaceStore('fusions')(fusionOutputId)(
      // @ts-ignore -- fix me
      'shapeSelection',
    ).mutateUsing((selection) =>
      shapeIndices.forEach((index) => delete selection[index]),
    );
  }

  private removeTransientInfo(shapeIndices: number[]) {
    shapeIndices.forEach((shapeIndex) =>
      this.taskWorkspaceStore('shapeTransientInfo')(shapeIndex).delete(),
    );
  }

  private getFrameNumber(outputId: number) {
    return this.fusionManagerFactory.getManager(outputId).fusionStore.state()
      .frameNumber;
  }
}

/** @deprecated used for old projects without objects */
export function createPlaceholderShapes(
  taskWorkspaceStore: TaskWorkspaceStore,
  pointCloudOutput: PointCloudOutput,
  shape: ShapeState,
) {
  if (!hasFusedAnswerOptions(taskWorkspaceStore, pointCloudOutput.id)) {
    return;
  }

  const { project, outputAnnotationInfo } = taskWorkspaceStore.state();
  getFusedNonOrthographicVideoOutputs(project, pointCloudOutput)
    // fix me if you change this code
    // do not overwrite placeholder / projection loaded from the answer

    // @ts-ignore -- fix me
    .filter((output) => !outputAnnotationInfo[output.name].shapes[shape.index])
    .forEach((output) => {
      createPlaceholderShape(taskWorkspaceStore, output.name, shape);
    });
}

/** @deprecated used for old projects without objects */
function createPlaceholderShape(
  taskWorkspaceStore: TaskWorkspaceStore,
  annotationOutputName: string,
  shape: ShapeState,
) {
  const fusedAnswerOptionNames = getFusedAnswerOptions(
    taskWorkspaceStore.state().project,
    annotationOutputName,
  ).map((answerOption) => answerOption.name);

  addUngroupedShape(
    {
      index: shape.index,
      type: 'rectangle',
      key_locations: shape.key_locations.map((keyLocation) => ({
        frame_number: keyLocation.frame_number,
        points: buildRectanglePoints([750, 240], [1050, 375]),
        visibility: 0,
      })),
      tags: pickExisting(shape.tags, ...fusedAnswerOptionNames),
      dynamic_tags: pickExisting(shape.dynamic_tags, ...fusedAnswerOptionNames),
    },
    // @ts-ignore -- fix me
    taskWorkspaceStore('outputAnnotationInfo')(annotationOutputName),
  );
}
